import {
	CONTACT_DIRECTORY_SCOPE,
	type ContactDirectoryScope,
} from "@/libraries/constant";
import { getTeamsQueryOptions } from "@/queries";
import type { CaseTeam, Group } from "@/types";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import compact from "lodash/compact";
import { useEffect, useMemo, useRef } from "react";

interface UseContactTeamsProps {
	isDirectory: boolean;
	search: string;
	scope?: ContactDirectoryScope;
	paddingStart?: number;
	paddingEnd?: number;
}

function useContactTeams(props: UseContactTeamsProps) {
	const {
		isDirectory,
		search,
		scope = CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
		paddingEnd,
		paddingStart,
	} = props;
	const scrollRef = useRef<HTMLDivElement>(null);
	const {
		data: resTeams,
		fetchNextPage,
		isError: isTeamsError,
		isFetching: isFetchingTeams,
		isLoading: isLoadingTeams,
		isFetchingNextPage,
		hasNextPage,
	} = useSuspenseInfiniteQuery(
		getTeamsQueryOptions({
			directory: isDirectory,
			scope,
		}),
	);

	const teams = useMemo(() => {
		let items =
			resTeams?.pages?.flatMap<CaseTeam | Group>(
				(page) => page?.teams || page?.groups || [],
			) ?? [];
		if (search) {
			items = items.filter((item) =>
				item.name?.toLowerCase()?.includes(search.toLowerCase()),
			);
		}
		return compact(items);
	}, [resTeams.pages, search]);

	const isLoading = useMemo(
		() => isLoadingTeams || isFetchingTeams || isFetchingNextPage,
		[isLoadingTeams, isFetchingTeams, isFetchingNextPage],
	);

	const isEmpty = useMemo(
		() => !isLoading && !props.search && !teams.length,
		[isLoading, teams, props.search],
	);

	const isEmptySearch = useMemo(
		() => !isLoading && !!props.search && !teams.length,
		[isLoading, teams, props.search],
	);

	const memberVirtualizer = useVirtualizer({
		count: teams.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 95,
		paddingStart,
		paddingEnd: teams.length ? paddingEnd : undefined,
		overscan: 5,
		gap: 16,
	});

	const memberVirtualizerItems = memberVirtualizer.getVirtualItems();
	useEffect(() => {
		const [lastItem] = [...memberVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= teams.length - 1 &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage();
		}
	}, [
		hasNextPage,
		fetchNextPage,
		teams.length,
		isFetchingNextPage,
		memberVirtualizerItems,
	]);

	return {
		scrollRef,
		teams,
		isError: isTeamsError,
		isLoading,
		isEmpty,
		isEmptySearch,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		virtualizer: {
			...memberVirtualizer,
			items: memberVirtualizerItems,
		},
	};
}

export default useContactTeams;
